/**
 * Maps the product data to the Google schema.
 * @param {Object} productData - The raw product data.
 * @param {Object} reviews - Customer review data
 */
export function createProductSchemaForSEO(productData, productPrice, reviews) {
  try {
    if (typeof window !== "undefined") {
      removeProductSchemaFromDOM();
      const description = `
    ${productData.stylistNotes?.replace(/<\/?[^>]+(>|$)/g, "")}. 
    ${productData.productDetails?.replace(/<\/?[^>]+(>|$)/g, "").trim()}. 
    ${productData.length ? `Length: ${productData.length}` : ""}.
  `.trim();

      const schema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": productData.designer?.displayName,
        "image": `https://pc-ap.rtrcdn.com/${productData.images?.front?.original}`,
        "description": description,
        "sku": productData.id,
        "brand": {
          "@type": "Brand",
          "name": productData.designer?.displayName,
        },
        "offers": {
          "@type": "Offer",
          "url": window.location.href,
          "priceCurrency": "USD",
          "availability": productData.published ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "price": productPrice,
        },
        "category": productData.category?.id,
        "size": productData.sizes?.join(", "),
        "color": productData.colors?.join(", "),
        "audience": {
          "@type": "PeopleAudience",
          "suggestedGender": "female",
          "suggestedAge": ["adult"],
        },
      };

      addProductSchemaToDOM(schema);

      updateProductSchemaWithReviews(reviews);
    }
  } catch (error) {
    window?.Sentry?.captureException("Error creating product schema for SEO:", error);
  }
}

export function updateProductSchemaWithReviews(reviews) {
  try {
    if (typeof window !== "undefined") {
      const schemaElement = document?.getElementById("product-schema");

      if (schemaElement && reviews?.data) {
        const schema = JSON.parse(schemaElement.textContent);

        // Sort and structure the top 10 reviews (can add more as required)
        const sortedReviews = [...reviews.data]
          .sort((a, b) => new Date(b?.uploadedAt) - new Date(a?.uploadedAt))
          .slice(0, 10)
          .map(review => ({
            "@type": "Review",
            "name": review?.caption || "User review",
            "datePublished": new Date(review?.uploadedAt),
            "reviewBody": review?.content,
            "reviewRating": {
              "@type": "Rating",
              "worstRating": 1,
              "bestRating": 5,
              "ratingValue": (review?.rating / 2).toFixed(1), // Convert to a 1-5 scale to allign with Google
            },
            "author": {
              "@type": "Person",
              "name": review?.firstName || "Anonymous",
            },
          }));

        schema.review = schema.review ? [...schema.review, ...sortedReviews] : sortedReviews;

        schema.aggregateRating = {
          "@type": "AggregateRating",
          "ratingValue": (reviews?.averageRating / 2).toFixed(1),
          "bestRating": 5,
          "reviewCount": reviews?.count,
        };

        schemaElement.textContent = JSON.stringify(schema);
        addProductSchemaToDOM(schema);
      }
    }
  } catch (error) {
    window?.Sentry?.captureException("SEO: Failed to create product-schema reviews with error:", error);
  }
}

/**
 * Adds the schema JSON-LD to the DOM.
 * @param {Object} schema - The schema.org structured data.
 */
function addProductSchemaToDOM(schema) {
  if (typeof window !== "undefined") {
    // Ensure the previous schema is removed before adding a new one
    removeProductSchemaFromDOM();
    const script = document?.createElement("script");
    script.type = "application/ld+json";
    script.id = "product-schema";
    script.textContent = JSON.stringify(schema);
    document?.head.appendChild(script);
  }
}

/**
 * Removes the schema when the PDP is closed.
 */
function removeProductSchemaFromDOM() {
  if (typeof window !== "undefined") {
    const schemaElement = document?.getElementById("product-schema");
    if (schemaElement) {
      schemaElement.remove();
    }
  }
}
